import { useCallback, useEffect, useState } from "react";
import type React from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { theme, card, btnSecondary, emptyFrame } from "../../theme";
import { clientOverviewPath } from "../../nav/consultantNav";
import { useConsultantShell } from "./ConsultantShellContext";
import { ScopeToggle } from "./ScopeToggle";
import { errText, type StaffMember } from "./workspace";
import { auditActionLabel } from "../../lib/auditAction";

// ── Activity ──────────────────────────────────────────────────────────────
// The audit log across the portfolio, newest first, filtered and paged on the
// server (GET /consultant/activity): a date range, a person, a client, a
// category and text. Firm-level entries (a client deleted, a firm setting
// changed) appear alongside, so nothing done at firm level drops out of sight.

interface ActivityEntry {
  id: string;
  organizationId: string | null;
  organizationName: string | null;
  userDisplayName: string | null;
  role: string;
  category: string;
  action: string;
  detail: string | null;
  createdAt: string;
}

const CATEGORIES = ["admin", "audit", "auth", "comment", "compliance", "document", "profile", "review", "schedule", "settings", "template"];

const CATEGORY_COLOR: Record<string, string> = {
  auth: theme.color.accent, review: theme.color.warning, document: theme.color.success, settings: theme.color.subtle,
  audit: theme.color.consultant, template: theme.color.accent, comment: theme.color.faint, profile: theme.color.subtle, schedule: theme.color.accent,
};

function when(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function ActivityPage() {
  const navigate = useNavigate();
  const { scope, portfolio } = useConsultantShell();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [client, setClient] = useState("");
  const [person, setPerson] = useState("");
  const [category, setCategory] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [entries, setEntries] = useState<ActivityEntry[] | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ staff: StaffMember[] }>("/consultant/workspace/staff").then((d) => setStaff(d.staff)).catch(() => undefined);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const query = useCallback((before?: string) => ({
    scope,
    organizationId: client || undefined,
    userId: person || undefined,
    category: category || undefined,
    from: from || undefined,
    to: to || undefined,
    q: q || undefined,
    before,
    limit: "50",
  }), [scope, client, person, category, from, to, q]);

  const load = useCallback(() => {
    setError(null);
    setEntries(null);
    api.get<{ activity: ActivityEntry[]; nextBefore: string | null }>("/consultant/activity", query())
      .then((d) => { setEntries(d.activity); setNextBefore(d.nextBefore); })
      .catch((e) => { setError(errText(e, "Could not load activity")); setEntries([]); });
  }, [query]);
  useEffect(load, [load]);

  const more = () => {
    if (!nextBefore) return;
    setLoadingMore(true);
    api.get<{ activity: ActivityEntry[]; nextBefore: string | null }>("/consultant/activity", query(nextBefore))
      .then((d) => { setEntries((prev) => [...(prev ?? []), ...d.activity]); setNextBefore(d.nextBefore); })
      .catch((e) => setError(errText(e, "Could not load more")))
      .finally(() => setLoadingMore(false));
  };

  const input: React.CSSProperties = { padding: "6px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12.5, maxWidth: 190 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actions" aria-label="Search activity" style={{ ...input, minWidth: 180 }} />
        <select value={client} onChange={(e) => setClient(e.target.value)} aria-label="Client" style={input}>
          <option value="">Every client</option>
          {portfolio.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Person" style={input}>
          <option value="">Anyone</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.displayName}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category" style={input}>
          <option value="">Every category</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
        <label style={{ fontSize: 12, color: theme.color.subtle, display: "flex", gap: 4, alignItems: "center" }}>
          From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={input} />
        </label>
        <label style={{ fontSize: 12, color: theme.color.subtle, display: "flex", gap: 4, alignItems: "center" }}>
          To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={input} />
        </label>
        <div style={{ marginLeft: "auto" }}><ScopeToggle compact /></div>
      </div>
      {from && to && from > to && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText }}>The start date is after the end date, so nothing can match.</div>}
      {person && <div style={{ fontSize: 11.5, color: theme.color.faint }}>Showing what this consultant did; entries by client members are not attributed to a consultant.</div>}

      {error && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
      {entries === null && !error && <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>}
      {entries && entries.length === 0 && !error && (
        <div style={{ ...emptyFrame, color: theme.color.muted, fontSize: 13, padding: 40 }}>No activity matches.</div>
      )}

      {entries && entries.length > 0 && (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          {entries.map((e, i) => {
            const color = CATEGORY_COLOR[e.category] ?? theme.color.faint;
            return (
              <div key={e.id} style={{ display: "flex", gap: 12, padding: "12px 18px", borderBottom: i < entries.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                <div style={{ width: 9, height: 9, borderRadius: "50%", background: color, marginTop: 5, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{auditActionLabel(e.action)}</span>
                    <span style={{ fontSize: 10.5, padding: "1px 7px", borderRadius: 4, fontWeight: 600, background: theme.color.panelAlt, color: theme.color.subtle, textTransform: "capitalize" }}>{e.category}</span>
                    {!e.organizationId && <span style={{ fontSize: 10.5, padding: "1px 7px", borderRadius: 4, fontWeight: 600, background: theme.color.consultantBg, color: theme.color.consultant }}>Firm</span>}
                  </div>
                  {e.detail && <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, wordBreak: "break-word" }}>{e.detail}</div>}
                  <div style={{ display: "flex", gap: 10, marginTop: 5, alignItems: "center", flexWrap: "wrap", fontSize: 11, color: theme.color.faint }}>
                    {e.organizationId && e.organizationName && (
                      <button onClick={() => navigate(clientOverviewPath(e.organizationId!))}
                        style={{ background: theme.color.accentBg, border: "none", borderRadius: 3, padding: "2px 8px", fontSize: 11, color: theme.color.accentText, cursor: "pointer", fontWeight: 600 }}>
                        {e.organizationName}
                      </button>
                    )}
                    {e.userDisplayName && <span>by {e.userDisplayName} ({e.role})</span>}
                    <span style={{ marginLeft: "auto" }} title={new Date(e.createdAt).toLocaleString("en-GB")}>{when(e.createdAt)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {nextBefore && (
        <div style={{ textAlign: "center" }}>
          <button onClick={more} disabled={loadingMore} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12.5 }}>{loadingMore ? "Loading…" : "Load more"}</button>
        </div>
      )}
    </div>
  );
}
